import type { TFunction } from 'i18next';
import type { FrameworkAction, FrameworkArgument, FrameworkClass } from '../framework/framework.d';

export type InputBindingDevice = 'controller' | 'drivebase';

export const INPUT_BINDING_KIND = {
  PRESSED: 'pressed',
  RELEASED: 'released',
  VARIABLE: 'variable',
  TOGGLE: 'toggle',
  UNSUPPORTED: 'unsupported',
} as const;

export type InputBindingKind = (typeof INPUT_BINDING_KIND)[keyof typeof INPUT_BINDING_KIND];
export type SingleActionInputBindingKind = Exclude<InputBindingKind, typeof INPUT_BINDING_KIND.TOGGLE | typeof INPUT_BINDING_KIND.UNSUPPORTED>;

export interface InputBindingAction {
  parentName: string;
  parentClass?: FrameworkClass;
  mechanismName?: string;
  mechanismClass?: FrameworkClass;
  action: FrameworkAction;
  unsupportedRawExpression?: string;
}

export interface BaseInputBinding {
  id: string;
  device: InputBindingDevice;
  button: string;
  /** 1-based line in program source when parsed from code (Monaco line numbers). */
  sourceLineNumber?: number;
}

export interface SingleActionInputBinding extends BaseInputBinding {
  kind: SingleActionInputBindingKind;
  action: InputBindingAction;
}

export interface ToggleInputBinding extends BaseInputBinding {
  kind: typeof INPUT_BINDING_KIND.TOGGLE;
  onAction: InputBindingAction;
  offAction: InputBindingAction;
}

export interface UnsupportedInputBinding extends BaseInputBinding {
  kind: typeof INPUT_BINDING_KIND.UNSUPPORTED;
  trigger: Exclude<InputBindingKind, typeof INPUT_BINDING_KIND.UNSUPPORTED>;
  rawCode: string;
  rawActionExpression: string;
  action: InputBindingAction;
}

export type InputBinding = SingleActionInputBinding | ToggleInputBinding | UnsupportedInputBinding;
export type InputBindingDraft = Omit<SingleActionInputBinding, 'id'> | Omit<ToggleInputBinding, 'id'> | Omit<UnsupportedInputBinding, 'id'>;
type InputBindingInput = InputBinding | InputBindingDraft;

export type InputBindingTableRow =
  | {
      id: string;
      bindingId: string;
      device: InputBindingDevice;
      button: string;
      kind: SingleActionInputBindingKind;
      labelKey: SingleActionInputBindingKind;
      action: InputBindingAction;
      binding: SingleActionInputBinding;
    }
  | {
      id: string;
      bindingId: string;
      device: InputBindingDevice;
      button: string;
      kind: typeof INPUT_BINDING_KIND.TOGGLE;
      labelKey: 'toggleOn' | 'toggleOff';
      side: 'on' | 'off';
      action: InputBindingAction;
      binding: ToggleInputBinding;
    }
  | {
      id: string;
      bindingId: string;
      device: InputBindingDevice;
      button: string;
      kind: typeof INPUT_BINDING_KIND.UNSUPPORTED;
      labelKey: UnsupportedInputBinding['trigger'];
      action: InputBindingAction;
      binding: UnsupportedInputBinding;
    };

function normalizePart(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

function normalizeArgValue(value: unknown): string {
  if (value === undefined) return '';
  if (value === null) return 'null';
  if (Array.isArray(value)) return `[${value.map(normalizeArgValue).join(',')}]`;
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .sort()
      .map((key) => `${normalizePart(key)}:${normalizeArgValue(record[key])}`)
      .join(',')}}`;
  }
  return String(value).trim();
}

function normalizeArg(arg: FrameworkArgument): string {
  const rawSourceExpression = arg?.rawSourceExpression === undefined ? '<none>' : String(arg.rawSourceExpression).trim();
  return `${normalizePart(arg?.name)}=${normalizeArgValue(arg?.value)}@raw=${rawSourceExpression}`;
}

function inputBindingActionSignature(action: InputBindingAction): string {
  const args = [...(action.action.arguments || [])].map(normalizeArg).sort().join('&');
  return [
    normalizePart(action.parentName),
    normalizePart(action.mechanismName),
    normalizePart(action.action.name),
    args,
    normalizePart(action.unsupportedRawExpression),
  ].join('|');
}

export function inputBindingSignature(binding: InputBindingInput): string {
  const base = [normalizePart(binding.device), normalizePart(binding.button), normalizePart(binding.kind)];
  if (binding.kind === INPUT_BINDING_KIND.TOGGLE) {
    return [...base, inputBindingActionSignature(binding.onAction), inputBindingActionSignature(binding.offAction)].join('|');
  }
  if (binding.kind === INPUT_BINDING_KIND.UNSUPPORTED) {
    return [...base, normalizePart(binding.trigger), String(binding.rawCode || '').trim()].join('|');
  }
  return [...base, inputBindingActionSignature(binding.action)].join('|');
}

export function createInputBindingId(binding: InputBindingInput): string {
  return `input-binding:${inputBindingSignature(binding)}`;
}

export function withInputBindingId<T extends InputBindingInput>(binding: T): T & { id: string } {
  return {
    ...binding,
    id: createInputBindingId(binding),
  };
}

export function dedupeInputBindingsBySignature<T extends InputBinding>(bindings: T[]): T[] {
  const seen = new Set<string>();
  const deduped: T[] = [];
  bindings.forEach((binding) => {
    const signature = inputBindingSignature(binding);
    if (seen.has(signature)) return;
    seen.add(signature);
    deduped.push(binding);
  });
  return deduped;
}

export function canUseVariableInputBinding(device: InputBindingDevice, button: string): boolean {
  const normalizedButton = String(button || '').toLowerCase();
  return device === 'controller' && ['l1', 'l2', 'r1', 'r2'].includes(normalizedButton);
}

export function isSupportedInputBindingForButtonEditor(binding: InputBinding, device: InputBindingDevice, button: string): boolean {
  return (
    binding.device === device &&
    String(binding.button || '') === button &&
    !hasUnsupportedInputBindingCode(binding) &&
    (binding.kind === INPUT_BINDING_KIND.TOGGLE ||
      binding.kind === INPUT_BINDING_KIND.PRESSED ||
      binding.kind === INPUT_BINDING_KIND.RELEASED ||
      (binding.kind === INPUT_BINDING_KIND.VARIABLE && canUseVariableInputBinding(device, button)))
  );
}

export function isUnsupportedInputBinding(binding: InputBinding): binding is UnsupportedInputBinding {
  return binding.kind === INPUT_BINDING_KIND.UNSUPPORTED;
}

export function getUnsupportedInputBindingActionExpression(action: InputBindingAction): string {
  return String(action.unsupportedRawExpression || '').trim();
}

export function isUnsupportedInputBindingAction(action: InputBindingAction): boolean {
  return getUnsupportedInputBindingActionExpression(action).length > 0;
}

export function hasUnsupportedInputBindingCode(binding: InputBinding): boolean {
  if (isUnsupportedInputBinding(binding)) return true;
  if (binding.kind === INPUT_BINDING_KIND.TOGGLE) {
    return isUnsupportedInputBindingAction(binding.onAction) || isUnsupportedInputBindingAction(binding.offAction);
  }
  return isUnsupportedInputBindingAction(binding.action);
}

export function canDeleteInputBindingFromCode(binding: InputBinding): boolean {
  if (!hasUnsupportedInputBindingCode(binding)) return true;
  return isUnsupportedInputBinding(binding) && binding.sourceLineNumber !== undefined;
}

export function getSupportedInputBindingsForButtonEditor(bindings: InputBinding[], device: InputBindingDevice, button: string): InputBinding[] {
  return bindings.filter((binding) => isSupportedInputBindingForButtonEditor(binding, device, button));
}

export function getInputBindingRowLabel(row: InputBindingTableRow, t: TFunction): string {
  switch (row.labelKey) {
    case INPUT_BINDING_KIND.RELEASED:
      return t('controller.buttonMapping.triggerReleased', 'Released');
    case INPUT_BINDING_KIND.VARIABLE:
      return t('controller.buttonMapping.triggerVariable', 'Variable');
    case 'toggleOn':
      return t('controller.buttonMapping.triggerToggleOn', 'Toggle On');
    case 'toggleOff':
      return t('controller.buttonMapping.triggerToggleOff', 'Toggle Off');
    case INPUT_BINDING_KIND.TOGGLE:
      return t('controller.buttonMapping.triggerToggle', 'Toggle');
    case INPUT_BINDING_KIND.PRESSED:
    default:
      return t('controller.buttonMapping.triggerPressed', 'Pressed');
  }
}

export function inputBindingsToTableRows(bindings: InputBinding[]): InputBindingTableRow[] {
  return bindings.flatMap((binding): InputBindingTableRow[] => {
    if (binding.kind === INPUT_BINDING_KIND.UNSUPPORTED) {
      return [
        {
          id: binding.id,
          bindingId: binding.id,
          device: binding.device,
          button: binding.button,
          kind: INPUT_BINDING_KIND.UNSUPPORTED,
          labelKey: binding.trigger,
          action: binding.action,
          binding,
        },
      ];
    }
    if (binding.kind === INPUT_BINDING_KIND.TOGGLE) {
      const rows: InputBindingTableRow[] = [
        {
          id: `${binding.id}:toggle-on`,
          bindingId: binding.id,
          device: binding.device,
          button: binding.button,
          kind: INPUT_BINDING_KIND.TOGGLE,
          labelKey: 'toggleOn' as const,
          side: 'on' as const,
          action: binding.onAction,
          binding,
        },
        {
          id: `${binding.id}:toggle-off`,
          bindingId: binding.id,
          device: binding.device,
          button: binding.button,
          kind: INPUT_BINDING_KIND.TOGGLE,
          labelKey: 'toggleOff' as const,
          side: 'off' as const,
          action: binding.offAction,
          binding,
        },
      ];
      return rows;
    }
    const row: InputBindingTableRow = {
      id: binding.id,
      bindingId: binding.id,
      device: binding.device,
      button: binding.button,
      kind: binding.kind,
      labelKey: binding.kind,
      action: binding.action,
      binding,
    };
    return [row];
  });
}
